import React, { useState, useEffect } from 'react';
import { auth, database, onAuthStateChanged, ref, get } from '../Auth/db/firebase';
import { Spin, notification, Button } from 'antd';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftOutlined, HomeOutlined } from '@ant-design/icons';
import './index.css';
import Items from './sections/items';
import CashierMode from './sections/CashierMode';
import Cashiers from './sections/cashiers';
import AdminNotificationCenter from '../../components/admin/AdminNotificationCenter';
import AdminMessaging from '../../components/admin/AdminMessaging';

const AdminDashboard = () => {
  const { facilityId } = useParams();
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [api, contextHolder] = notification.useNotification();

  const openNotification = (type, message) => {
    api[type]({
      message,
      placement: 'bottomRight',
      style: {
        background: 'var(--surface-elevated)',
        color: 'var(--text-primary)',
        border: '1px solid var(--primary-color)',
        borderRadius: '8px',
        boxShadow: 'var(--shadow-medium)',
      },
    });
  };

  // Set page title
  useEffect(() => {
    document.title = `${facilityId} - Admin Dashboard | KioStock`;
  }, [facilityId]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          // First, try to get user data from the specific facility
          let userSnapshot = await get(ref(database, `users/${facilityId}/${user.uid}`));
          
          if (userSnapshot.exists()) {
            setUserData({ ...userSnapshot.val(), facilityId });
          } else {
            // If not found in the specific facility, check if user is a superadmin in the 'me' collection
            const meSnapshot = await get(ref(database, `users/me`));
            if (meSnapshot.exists() && meSnapshot.val().email === user.email && meSnapshot.val().role === 'superadmin') {
              setUserData({ 
                ...meSnapshot.val(), 
                facilityId, 
                isSuperAdminVisiting: true 
              });
              openNotification('info', `Accessing ${facilityId} admin page as Super Admin`);
            } else {
              // Check all facilities for superadmin
              const allUsersSnapshot = await get(ref(database, 'users'));
              let foundSuperAdmin = false;
              
              if (allUsersSnapshot.exists()) {
                const facilities = allUsersSnapshot.val();
                for (const fid in facilities) {
                  if (fid === 'me') continue;
                  const users = facilities[fid];
                  for (const uid in users) {
                    if (users[uid].email === user.email && users[uid].role === 'superadmin') {
                      setUserData({ 
                        ...users[uid], 
                        facilityId, 
                        originalFacilityId: fid,
                        isSuperAdminVisiting: true 
                      });
                      openNotification('info', `Accessing ${facilityId} admin page as Super Admin`);
                      foundSuperAdmin = true;
                      break;
                    }
                  }
                  if (foundSuperAdmin) break;
                }
              }
              
              if (!foundSuperAdmin) {
                openNotification('error', 'User data not found or insufficient permissions.');
                setUserData(null);
              }
            }
          }
        } catch (err) {
          openNotification('error', `Error fetching user data: ${err.message}`);
          setUserData(null);
        }
      }
      setLoading(false);
    });

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityId]);

  if (loading) {
    return <Spin size="large" style={{ display: 'block', margin: '50px auto' }} />;
  }

  if (!userData) {
    return <div className="admin-container">No user data available.</div>;
  }

  return (
    <div className="admin-container">
      {contextHolder}
      <div className="admin-page-header">
        <h1 className="admin-page-title">{facilityId} Dashboard</h1>
        <div className="admin-page-actions">
          <Button
            type="default"
            icon={<HomeOutlined />}
            onClick={() => navigate(`/${facilityId}/`)}
            size="large"
          >
            Home
          </Button>
          <AdminMessaging facilityId={facilityId} userData={userData} />
          <AdminNotificationCenter 
            facilityId={facilityId} 
            uid={auth.currentUser?.uid} 
          />
          {userData.isSuperAdminVisiting && (
            <Button
              type="primary"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/superadmin')}
              size="large"
            >
              Back to Super Admin
            </Button>
          )}
        </div>
      </div>
      {userData.isSuperAdminVisiting && (
        <div className="info-alert" style={{ marginBottom: '24px' }}>
          <strong>Super Admin Access</strong> - You are viewing {facilityId} admin dashboard as a Super Admin
          {userData.originalFacilityId && ` (originally from ${userData.originalFacilityId})`}
        </div>
      )}
      <Items userData={userData} />
      <Cashiers />
      <CashierMode />
    </div>
  );
};

export default AdminDashboard;